import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";

import type { MobileWindowId } from "./MobileWindowState";

/** History state key naming the window an entry belongs to. */
export const WINDOW_STATE_KEY = "mobileWindow";
/** Router location key the entry was pushed over (a same-URL entry). */
const LOCATION_STATE_KEY = "mobileWindowLocation";

// Pops we triggered ourselves to drop a consumed entry: not a user "back".
// Module level: it must survive the layout's remount on a locale change.
let selfPops = 0;

const windowInHistory = (locationKey: string): MobileWindowId | null => {
  const state = window.history.state as Record<string, unknown> | null;
  const id = state?.[WINDOW_STATE_KEY];
  return typeof id === "string" && state?.[LOCATION_STATE_KEY] === locationKey
    ? (id as MobileWindowId)
    : null;
};

/**
 * Ties the open mobile window to the browser history, like an iOS modal:
 *
 * - Opening pushes a same-URL entry naming it, so Back (button, edge swipe,
 *   Android back) closes the window instead of leaving the page.
 * - Closing it any other way (x, Escape, swipe down) consumes that entry, so
 *   history stays balanced: call the returned `consume` first.
 * - A route change (a link inside the window) closes it; coming back to the
 *   entry later (Back from the changelog, a task opened from the agenda)
 *   presents the window again, as it was left.
 *
 * Only same-URL entries pushed over the current router location count: the
 * router writes its own state on every navigation, dropping the key.
 */
export const useWindowHistory = (
  active: MobileWindowId | null,
  setActive: (active: MobileWindowId | null) => void,
): (() => void) => {
  const { key: locationKey, pathname } = useLocation();
  const activeRef = useRef(active);
  const setActiveRef = useRef(setActive);
  const locationRef = useRef(locationKey);
  const pathRef = useRef(pathname);
  useEffect(() => {
    activeRef.current = active;
    setActiveRef.current = setActive;
    locationRef.current = locationKey;
  });

  // A route change closes the window, or restores the one its entry names.
  useEffect(() => {
    if (pathRef.current === pathname) return;
    pathRef.current = pathname;
    setActiveRef.current(windowInHistory(locationKey));
  }, [pathname, locationKey]);

  useEffect(() => {
    if (!active || windowInHistory(locationKey) === active) return;
    window.history.pushState(
      {
        ...window.history.state,
        [WINDOW_STATE_KEY]: active,
        [LOCATION_STATE_KEY]: locationKey,
      },
      "",
    );
    // Only on open: a later location change closes the window anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    const onPopState = () => {
      if (selfPops > 0) {
        selfPops -= 1;
        return;
      }
      const target = windowInHistory(locationRef.current);
      if (target !== activeRef.current) setActiveRef.current(target);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return useCallback(() => {
    const current = activeRef.current;
    if (current && windowInHistory(locationRef.current) === current) {
      selfPops += 1;
      window.history.back();
    }
  }, []);
};
